/**
 * Logik des Army Composers (Masterprompt Abschnitte 70, 74).
 *
 * Hier steht alles, was der Composer ANZEIGT und wie man eine Welle BEARBEITET:
 * Creeps hinzufuegen und entfernen, Kosten, Army Points, Income-Vorschau und der
 * Verlauf der letzten Wellen.
 *
 * Wichtig: Das ist nur eine Vorschau. Ob eine Welle wirklich gesendet wird,
 * entscheidet der Server beim SEND - mit denselben Regeln aus @dueline/game-rules.
 *
 * Die Logik steht bewusst ausserhalb der Oberflaeche, damit sie ohne Browser
 * pruefbar ist.
 */

import {
  CREEPS,
  MAX_SENT_CREEPS_PER_LANE,
  TIER_UPGRADE_COST,
  WAVE_HISTORY_SIZE,
  getCreep,
  type CreepDefinition,
  type CreepTier,
} from '@dueline/game-data';
import {
  calculateWaveCost,
  calculateWaveIncomeGain,
  flattenWave,
  getArmyPointCap,
  getCreepRow,
  validateWave,
  type WaveRow,
  type WaveValidationError,
} from '@dueline/game-rules';

/** Eine Welle im Composer: pro Reihe eine Liste von Creep-IDs. */
export interface WaveDraft {
  front: string[];
  center: string[];
  back: string[];
}

export const WAVE_ROWS: readonly WaveRow[] = ['front', 'center', 'back'];

export function emptyDraft(): WaveDraft {
  return { front: [], center: [], back: [] };
}

/** Eine unabhaengige Kopie - der Composer veraendert nie eine fremde Welle. */
export function copyDraft(draft: WaveDraft): WaveDraft {
  return { front: [...draft.front], center: [...draft.center], back: [...draft.back] };
}

export function isDraftEmpty(draft: WaveDraft): boolean {
  return draft.front.length === 0 && draft.center.length === 0 && draft.back.length === 0;
}

/** Fuegt einen Creep am Ende einer Reihe hinzu. */
/**
 * Legt einen Creep in die Welle.
 *
 * Die Reihe waehlt niemand mehr aus - sie steht am Creep fest
 * (Produktentscheidung vom 27.09.2026, siehe wave-rows.ts).
 */
export function addCreep(draft: WaveDraft, creepId: string): WaveDraft {
  const next = copyDraft(draft);
  next[getCreepRow(creepId)].push(creepId);
  return next;
}

/** Entfernt EINEN Creep dieser Art - den zuletzt hinzugefuegten. */
export function removeCreep(draft: WaveDraft, creepId: string): WaveDraft {
  const next = copyDraft(draft);
  const row = getCreepRow(creepId);
  const index = next[row].lastIndexOf(creepId);
  if (index >= 0) next[row].splice(index, 1);
  return next;
}

/** Zwei Wellen sind gleich, wenn jede Reihe dieselben Creeps in derselben Reihenfolge hat. */
export function isSameDraft(a: WaveDraft, b: WaveDraft): boolean {
  return WAVE_ROWS.every(
    (row) => a[row].length === b[row].length && a[row].every((id, i) => id === b[row][i]),
  );
}

/** Creeps einer Reihe, zusammengefasst: "Minion x3, Brute x1" - in Reihenfolge des Auftretens. */
export function groupRow(ids: readonly string[]): { creepId: string; count: number }[] {
  const groups: { creepId: string; count: number }[] = [];
  for (const id of ids) {
    const group = groups.find((g) => g.creepId === id);
    if (group) group.count += 1;
    else groups.push({ creepId: id, count: 1 });
  }
  return groups;
}

/** Creeps, die bei diesem Tier gesendet werden duerfen - in der Reihenfolge der Spieldaten. */
export function getAvailableCreeps(tier: number): CreepDefinition[] {
  return CREEPS.filter((creep) => creep.tier <= tier);
}

/** Alles, was die Fusszeile des Composers anzeigt. */
export interface DraftSummary {
  cost: number;
  incomeGain: number;
  /** Income nach dem SEND. */
  newIncome: number;
  armyPoints: number;
  armyPointCap: number;
  /** Nach den Regeln zulaessig? (Gold spielt hier keine Rolle.) */
  valid: boolean;
  errors: WaveValidationError[];
  /** Reicht das Gold jetzt gerade? */
  affordable: boolean;
}

export function summarizeDraft(
  draft: WaveDraft,
  player: { gold: number; income: number; tier: number },
): DraftSummary {
  const validation = validateWave(draft, player.tier as CreepTier);
  const ids = flattenWave(draft);

  // Unbekannte IDs koennen hier praktisch nicht vorkommen - der Composer bietet
  // nur echte Creeps an. Falls doch, rechnet die Vorschau sie mit 0.
  let cost = 0;
  let incomeGain = 0;
  if (!validation.errors.includes('unknown_creep')) {
    cost = calculateWaveCost(ids);
    incomeGain = calculateWaveIncomeGain(ids);
  }

  return {
    cost,
    incomeGain,
    newIncome: player.income + incomeGain,
    armyPoints: validation.armyPoints,
    armyPointCap: validation.armyPointCap,
    valid: validation.valid,
    errors: validation.errors,
    affordable: player.gold >= cost,
  };
}

/** Warum SEND gerade nicht geht - oder null, wenn es geht. */
/**
 * Kann dieser Creep jetzt noch in die Welle?
 *
 * Rueckmeldung aus dem Spieltest (24.09.2026): Man konnte Creeps in die Welle
 * legen, die man gar nicht bezahlen kann - und merkte es erst beim SEND.
 * Jetzt entscheidet sich das schon an der Karte.
 */
export function canAddCreep(
  creep: CreepDefinition,
  draft: WaveDraft,
  player: { gold: number; tier: number },
  creepsInTargetLane = 0,
): {
  allowed: boolean;
  reason: 'tier_locked' | 'not_enough_gold' | 'too_many_points' | 'lane_full' | null;
} {
  if (creep.tier > player.tier) return { allowed: false, reason: 'tier_locked' };

  const ids = flattenWave(draft);
  const bereitsKosten = calculateWaveCost(ids);
  if (bereitsKosten + creep.cost > player.gold) {
    return { allowed: false, reason: 'not_enough_gold' };
  }

  const punkte = ids.reduce((summe, id) => summe + (getCreep(id)?.armyPoints ?? 0), 0);
  if (punkte + creep.armyPoints > getArmyPointCap(player.tier as CreepTier)) {
    return { allowed: false, reason: 'too_many_points' };
  }

  if (creepsInTargetLane + ids.length + 1 > MAX_SENT_CREEPS_PER_LANE) {
    return { allowed: false, reason: 'lane_full' };
  }

  return { allowed: true, reason: null };
}

export type SendBlocker =
  | 'match_not_running'
  | 'empty'
  | 'too_many_points'
  | 'too_many_bosses'
  | 'tier_locked'
  | 'invalid'
  | 'not_enough_gold'
  | 'send_cooldown'
  | 'lane_full';

export function getSendBlocker(
  summary: DraftSummary,
  battleRunning: boolean,
  /** Restliche Wartezeit in Sekunden. */
  cooldownRemaining = 0,
  /** Creeps, die schon auf die gegnerische Lane zulaufen. */
  creepsInTargetLane = 0,
  /** Anzahl Creeps im Entwurf. */
  draftSize = 0,
): SendBlocker | null {
  if (summary.errors.includes('empty_wave')) return 'empty';
  // Der genaueste Grund zuerst - damit klar ist, was zu aendern ist.
  if (summary.errors.includes('creep_tier_locked')) return 'tier_locked';
  if (summary.errors.includes('too_many_bosses')) return 'too_many_bosses';
  if (summary.errors.includes('army_cap_exceeded')) return 'too_many_points';
  if (!summary.valid) return 'invalid';
  if (!battleRunning) return 'match_not_running';
  if (!summary.affordable) return 'not_enough_gold';
  if (cooldownRemaining > 0) return 'send_cooldown';
  if (creepsInTargetLane + draftSize > MAX_SENT_CREEPS_PER_LANE) return 'lane_full';
  return null;
}

/**
 * Traegt eine bestaetigte Welle in den Verlauf ein (Abschnitt 74: letzte drei).
 *
 * Die neueste steht vorn. Wird eine Welle erneut gesendet, rueckt sie nach vorn,
 * statt den Verlauf mit Doppelten zu fuellen.
 */
export function addToHistory(
  history: readonly WaveDraft[],
  wave: WaveDraft,
  size = WAVE_HISTORY_SIZE,
): WaveDraft[] {
  const ohneDoppelte = history.filter((entry) => !isSameDraft(entry, wave));
  return [copyDraft(wave), ...ohneDoppelte].slice(0, size);
}

/** Kurzform eines Creeps fuer das Platzhalter-Symbol: "Minion" -> "MI". */
export function creepInitials(name: string): string {
  const woerter = name.split(/\s+/).filter(Boolean);
  if (woerter.length >= 2) return `${woerter[0]?.[0] ?? ''}${woerter[1]?.[0] ?? ''}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/** Alle kaufbaren Creeps eines bestimmten Tiers - fuer die Reiter im Composer. */
export function getCreepsOfTier(tier: number): CreepDefinition[] {
  return CREEPS.filter((creep) => creep.tier === tier);
}

/** Wie ein Tier-Reiter aus Sicht des Spielers aussieht. */
export type TierTabState =
  | { kind: 'unlocked' }
  /** Das naechste Tier: kann jetzt gekauft werden, wenn das Gold reicht. */
  | { kind: 'next'; cost: number; affordable: boolean }
  /** Ein spaeteres Tier: erst das davor freischalten. */
  | { kind: 'later'; requiresTier: number };

export function getTierTabState(tabTier: number, playerTier: number, gold: number): TierTabState {
  if (tabTier <= playerTier) return { kind: 'unlocked' };
  if (tabTier === playerTier + 1) {
    const cost = TIER_UPGRADE_COST[tabTier as 2 | 3 | 4];
    return { kind: 'next', cost, affordable: gold >= cost };
  }
  return { kind: 'later', requiresTier: tabTier - 1 };
}

/**
 * Welcher Tier-Reiter gerade offen ist.
 *
 * Der Reiter ALL ist am 27.09.2026 wieder entfallen: Man soll bewusst in ein
 * Tier gehen, um daraus zu schicken.
 */
export type TierTab = 1 | 2 | 3 | 4;

/** Die Creeps eines Reiters. */
export function getCreepsForTab(tab: TierTab): CreepDefinition[] {
  return getCreepsOfTier(tab);
}
